/**
 * Conflict Detection Panel
 *
 * Detects in real-time:
 * 1. Actor double-booked — same actor in two scenes on the same day at different locations
 * 2. Day/Night mismatch — EXT DAY scene scheduled on same day as EXT NIGHT (continuity)
 * 3. Turnaround violation — night scenes followed immediately by dawn call
 * 4. Flagged scene without prep — stunt/explosion scene has no prep day before it
 * 5. Missing weather cover — EXT scenes in monsoon months with no INT backup
 * 6. Child actor overload — more than 4 pages of child actor scenes in a day
 */
import React, { useMemo } from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle2, Zap } from 'lucide-react';
import { formatSceneNumber } from '../../utils/sceneNumber';

const MONSOON_MONTHS_ISO = ['06','07','08','09','10']; // June–October in Kerala

function isMonsoonDate(dateStr) {
  if (!dateStr) return false;
  const mm = dateStr.slice(5, 7);
  return MONSOON_MONTHS_ISO.includes(mm);
}

function detectConflicts(days, breakdownById) {
  const conflicts = [];

  days.forEach((day, dayIdx) => {
    const sceneIds = day.scene_ids || [];
    const scenes = sceneIds.map(id => breakdownById[id]).filter(Boolean);

    // ── 1. Actor double-booked at different locations ──────────────────────
    const castByLocation = {};
    scenes.forEach(s => {
      const loc = (s.location_name || '').trim();
      (s.characters_in_scene || []).forEach(actor => {
        if (!castByLocation[actor]) castByLocation[actor] = new Set();
        castByLocation[actor].add(loc);
      });
    });
    Object.entries(castByLocation).forEach(([actor, locs]) => {
      if (locs.size > 1) {
        conflicts.push({
          id: `dbl-${day.id}-${actor}`,
          type: 'actor_conflict',
          severity: 'error',
          dayId: day.id,
          dayNumber: day.day_number,
          title: `Actor double-booked`,
          detail: `${actor} is needed at ${Array.from(locs).join(' AND ')} on Day ${day.day_number}`,
          suggestion: `Split ${actor}'s scenes across different days`,
          sceneId: null,
        });
      }
    });

    // ── 2. Day/Night mismatch — same actor in DAY and NIGHT scene same day ─
    const actorDay = {};   // actor → [DAY scenes]
    const actorNight = {}; // actor → [NIGHT scenes]
    scenes.forEach(s => {
      const tod = (s.time_of_day || 'DAY').toUpperCase();
      (s.characters_in_scene || []).forEach(actor => {
        if (tod === 'DAY') (actorDay[actor] = actorDay[actor] || []).push(s);
        if (tod === 'NIGHT') (actorNight[actor] = actorNight[actor] || []).push(s);
      });
    });
    Object.keys(actorDay).forEach(actor => {
      if (actorNight[actor] && actorNight[actor].length > 0) {
        conflicts.push({
          id: `dn-${day.id}-${actor}`,
          type: 'day_night_mismatch',
          severity: 'warning',
          dayId: day.id,
          dayNumber: day.day_number,
          title: 'Day + Night scenes same day',
          detail: `${actor} shoots both DAY and NIGHT scenes on Day ${day.day_number} — this creates a split day or turnaround risk`,
          suggestion: 'Separate day and night scenes into different shoot days, or explicitly plan a split day',
          sceneId: null,
        });
      }
    });

    // ── 3. Flagged stunt/explosion scene without prep day ──────────────────
    const flaggedScenes = scenes.filter(s => s.is_flagged);
    flaggedScenes.forEach(s => {
      const prevDay = days[dayIdx - 1];
      const hasPrepDay = prevDay && (
        prevDay.scene_ids?.length === 0 ||
        (prevDay.notes || '').toLowerCase().includes('prep') ||
        (prevDay.notes || '').toLowerCase().includes('stunt')
      );
      if (!hasPrepDay && (s.flag_reason || '').toLowerCase().match(/stunt|explosion|fire|pyro|gun/)) {
        conflicts.push({
          id: `prep-${day.id}-${s.id}`,
          type: 'missing_prep',
          severity: 'warning',
          dayId: day.id,
          dayNumber: day.day_number,
          title: 'Stunt/SFX without prep day',
          detail: `Scene ${formatSceneNumber(s.scene_number)} (${s.flag_reason}) on Day ${day.day_number} — stunt coordinator prep day recommended the day before`,
          suggestion: `Add a prep day (Day ${day.day_number - 1}) with "Stunt Prep" notes`,
          sceneId: s.id,
        });
      }
    });

    // ── 4. EXT scene in monsoon month without INT weather cover ───────────
    if (day.shoot_date && isMonsoonDate(day.shoot_date)) {
      const extScenes = scenes.filter(s => (s.location_type || '').toUpperCase() === 'EXT');
      const intScenes = scenes.filter(s => (s.location_type || '').toUpperCase() === 'INT');
      if (extScenes.length > 0 && intScenes.length === 0) {
        conflicts.push({
          id: `monsoon-${day.id}`,
          type: 'no_weather_cover',
          severity: 'info',
          dayId: day.id,
          dayNumber: day.day_number,
          title: 'EXT scenes in monsoon — no weather cover',
          detail: `Day ${day.day_number} (${day.shoot_date}) has ${extScenes.length} EXT scene(s) in Kerala monsoon season with no INT backup`,
          suggestion: 'Add INT scenes from the same location as weather cover (rain alternative)',
          sceneId: null,
        });
      }
    }

    // ── 5. Dawn/Dusk scenes — more than 2 per day ────────────────────────
    const goldenHourScenes = scenes.filter(s => {
      const tod = (s.time_of_day || '').toUpperCase();
      return tod === 'DAWN' || tod === 'DUSK';
    });
    if (goldenHourScenes.length > 2) {
      conflicts.push({
        id: `golden-${day.id}`,
        type: 'too_many_golden_hour',
        severity: 'warning',
        dayId: day.id,
        dayNumber: day.day_number,
        title: 'Too many golden-hour scenes',
        detail: `Day ${day.day_number} has ${goldenHourScenes.length} dawn/dusk scenes. Golden hour is only ~20 minutes — maximum 2 per day`,
        suggestion: 'Distribute dawn/dusk scenes across multiple days',
        sceneId: null,
      });
    }

    // ── 6. Page count overload (Malayalam industry: max ~10p/day) ─────────
    const totalPages = scenes.reduce((sum, s) => sum + (s.page_count_eighths || 0), 0) / 8;
    if (totalPages > 10) {
      conflicts.push({
        id: `pages-${day.id}`,
        type: 'page_overload',
        severity: 'info',
        dayId: day.id,
        dayNumber: day.day_number,
        title: 'Exceeds Malayalam industry page limit',
        detail: `Day ${day.day_number} has ${totalPages.toFixed(1)} pages. Malayalam film industry norm is 8–10 pages/day (FEFKA union guideline)`,
        suggestion: 'Move some scenes to adjacent days to keep under 10 pages',
        sceneId: null,
      });
    }
  });

  return conflicts;
}

const SEVERITY_CONFIG = {
  error:   { icon: AlertCircle,   color: 'text-red-400',    bg: 'bg-red-500/10 border-red-500/30',    label: 'Error' },
  warning: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', label: 'Warning' },
  info:    { icon: Info,          color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/30',  label: 'Info' },
};

export default function ConflictPanel({ days, breakdownById, onJumpToDay }) {
  const conflicts = useMemo(() => detectConflicts(days, breakdownById), [days, breakdownById]);

  const errors   = conflicts.filter(c => c.severity === 'error');
  const warnings = conflicts.filter(c => c.severity === 'warning');
  const infos    = conflicts.filter(c => c.severity === 'info');

  if (conflicts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <CheckCircle2 className="h-8 w-8 text-emerald-400 mb-2" />
        <p className="text-sm text-[#f0f0f0] font-medium">No conflicts detected</p>
        <p className="text-xs text-[#a0a0a0] mt-1">Your schedule looks clean!</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Summary */}
      <div className="flex items-center gap-3 pb-2 border-b border-[#1e1e1e]">
        {errors.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-red-400">
            <AlertCircle className="h-3 w-3" /> {errors.length} error{errors.length > 1 ? 's' : ''}
          </span>
        )}
        {warnings.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-yellow-400">
            <AlertTriangle className="h-3 w-3" /> {warnings.length} warning{warnings.length > 1 ? 's' : ''}
          </span>
        )}
        {infos.length > 0 && (
          <span className="flex items-center gap-1 text-xs text-blue-400">
            <Info className="h-3 w-3" /> {infos.length} suggestion{infos.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Conflict list */}
      {conflicts.map(c => {
        const cfg = SEVERITY_CONFIG[c.severity];
        const Icon = cfg.icon;
        return (
          <div
            key={c.id}
            className={`rounded-lg border p-3 space-y-1.5 ${cfg.bg}`}
            data-testid={`conflict-${c.id}`}
          >
            <div className="flex items-start gap-2">
              <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${cfg.color}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs font-semibold ${cfg.color}`}>{c.title}</span>
                  <button
                    onClick={() => onJumpToDay && onJumpToDay(c.dayId)}
                    className="text-[9px] text-[#555] hover:text-[#e8c547] transition-colors shrink-0"
                  >
                    Day {c.dayNumber} →
                  </button>
                </div>
                <p className="text-[11px] text-[#a0a0a0] leading-tight mt-0.5">{c.detail}</p>
                {c.suggestion && (
                  <p className="text-[10px] text-[#666] italic mt-1 flex items-start gap-1">
                    <Zap className="h-2.5 w-2.5 mt-0.5 text-[#e8c547] shrink-0" />
                    {c.suggestion}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { detectConflicts };
